import { Button } from '@/components/ui/Button';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import * as React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  className,
}) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages();

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {/* Info */}
      {totalItems && itemsPerPage && (
        <div className='text-sm text-grey-700'>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}{' '}
          to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{' '}
          results
        </div>
      )}

      {/* Pagination */}
      <div className='flex items-center space-x-1'>
        <Button
          icon={mdiChevronLeft}
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          color='light'
          small
          className='px-2'
        />

        {visiblePages.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className='px-3 py-1 text-grey-500'>...</span>
            ) : (
              <Button
                label={page.toString()}
                onClick={() => onPageChange(page as number)}
                color={currentPage === page ? 'contrast' : 'light'}
                small
                className='min-w-[36px]'
              />
            )}
          </React.Fragment>
        ))}

        <Button
          icon={mdiChevronRight}
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          color='light'
          small
          className='px-2'
        />
      </div>
    </div>
  );
};
